
import { useEffect, useState } from "react";

import Lightbox from "../../common/Lightbox";

import useDisplayNotifications from "../../../hooks/useDisplayNotifications";
import { ITask } from "../../../data_models/task";


import useCurrentUser from "../../../hooks/useCurrentUser";
import { EmployerWallet } from "../../../data_models/employerWallet";
import LoadingIndicator from "../../common/LoadingIndicator";
import useRouting from "../../../hooks/useRouting";
import { ErrorBox } from "../../common/InfoBox";
import useTasks from "../../../hooks/useTasks";


interface StartAndPayTaskLightboxProps {
    task: ITask;

    taskId?: string;
    onClose: () => void;
    onUpdateTask: (updatedTask: ITask) => void;
}

const StartAndPayTaskLightbox: React.FC<StartAndPayTaskLightboxProps> = ({ task, onClose, onUpdateTask }) => {

    const { startAndPayTask } = useTasks();
    const { goToPricing } = useRouting();
    const { wallet, locallyUpdateCurrentUserWallet } = useCurrentUser();

    const [requiredCredits, setRequiredCredits] = useState<number>();
    const [availableCredits, setAvailableCredits] = useState<number>();

    const [notEnoughCreditsError, setNotEnoughCreditsError] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { addSuccessNotification, addErrorNotification } = useDisplayNotifications();

    useEffect(() => {

        setRequiredCredits(task.estimatedCredits || task.actualCredits || 0);
        const available = (wallet as EmployerWallet)?.totalCredits - (wallet as EmployerWallet)?.totalOccupiedCredits;
        setAvailableCredits(available);
    }, [wallet]);

    useEffect(() => {
        if (availableCredits === undefined || requiredCredits === undefined) {
            return;
        }

        if (availableCredits < requiredCredits) {
            setNotEnoughCreditsError(true);
        } else {
            setNotEnoughCreditsError(false);
        }
    }, [availableCredits, requiredCredits]);




    const onStartAndPayTask = async () => {

        setIsLoading(true);
        try {
            const result = await startAndPayTask(task._id!);
            setIsLoading(false);
            onUpdateTask(result.task);
            locallyUpdateCurrentUserWallet(result.wallet);
            addSuccessNotification("Successfully paid for this Task. Talent can now start working on it.");
        } catch (error: any) {

            addErrorNotification(error.message, "Payment Failed");
        }
        setIsLoading(false);
        onClose();
    }

    const onPurchaseCredits = () => {
        goToPricing();
        onClose();
    }

    return (

        <Lightbox
            header={`Complete Payment (${requiredCredits} Hrs)`}
            onClose={onClose}
            onCancel={onClose}
            onConfirm={onStartAndPayTask}
            onThirdAction={onPurchaseCredits}
            thirdActionText="Buy Hrs"
            disabledConfirm={notEnoughCreditsError !== false}
            loadingConfirm={isLoading}
            content={
                <div className="flex flex-col ƒitems-start space-y-6" >
                    {notEnoughCreditsError === null &&
                        <div className="flex flex-row justify-center items-center w-full">
                            <LoadingIndicator />
                        </div>}
                    {notEnoughCreditsError === true &&
                        <ErrorBox>
                            <p>Unfortunately, you do not have enough credits available in your wallet. Please purchase more and try again.</p>
                            <p className="mt-2">You have {availableCredits} credits available, but you need {requiredCredits} credits.</p>
                        </ErrorBox>
                    }
                    {notEnoughCreditsError === false &&
                        <p>You have enough credits. Confirm your payment for <span className="font-bold">{task.projectRank}. {task.title}</span> from your wallet to get started with this task.</p>
                    }
                </div>}

        />

    );
}

export default StartAndPayTaskLightbox;
